import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { fetchHostConversations, markConversationSeen, type HostConversationSummary } from '../../lib/hostConversations';
import { ConversationList } from '../../components/host/ConversationList';
import { IconButton, SearchInput, Spinner, Tab } from '../../components/design-system';
import { HostChat } from './HostChat';

type Filter = 'all' | 'unread' | 'inquiries' | 'active';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'inquiries', label: 'Inquiries' },
  { key: 'active', label: 'On trip' },
];

/**
 * HostChats — inbox: two-pane conversation list + active thread.
 * Design source: Host Chats.dc.html
 *
 * Desktop (md+): list pane and thread pane render side by side.
 * Mobile: list-only at /host/chats; selecting a conversation navigates to
 * /host/chats/:matchId which shows thread-only with a back button.
 *
 * The thread pane reuses the existing HostChat page (fetch/send/realtime
 * untouched) — this page adds the list pane, search, filter pills, and the
 * responsive two-pane shell.
 */
export function HostChats() {
  const { matchId } = useParams<{ matchId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState<HostConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    if (!user) return;
    const rows = await fetchHostConversations(user.id);
    setConversations(rows);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  // Mark the selected conversation as seen (clears its unread badge).
  useEffect(() => {
    if (!matchId) return;
    const conv = conversations.find((c) => c.matchId === matchId);
    if (conv?.conversationId && conv.unread) {
      markConversationSeen(conv.conversationId);
      setConversations((prev) => prev.map((c) => (c.matchId === matchId ? { ...c, unread: false } : c)));
    }
  }, [matchId, conversations]);

  const unreadTotal = conversations.filter((c) => c.unread).length;

  // Filter + search + unread-first sort.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return conversations
      .filter((c) => {
        if (filter === 'unread' && !c.unread) return false;
        if (filter === 'inquiries' && c.stage !== 'inquiry') return false;
        if (filter === 'active' && c.stage !== 'active') return false;
        if (q && !((c.travelerName ?? '').toLowerCase().includes(q) || (c.tripName ?? '').toLowerCase().includes(q))) return false;
        return true;
      })
      .sort((a, b) => (a.unread ? 0 : 1) - (b.unread ? 0 : 1) || (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  }, [conversations, filter, search]);

  function handleSelect(nextMatchId: string) {
    navigate(`/host/chats/${nextMatchId}`);
  }


  return (
    <div className="flex h-full min-h-0 bg-app-bg">
      {/* List pane — always visible on desktop; mobile shows it only when no thread is selected */}
      <div className={`w-full md:w-[350px] md:shrink-0 md:border-r md:border-app-border flex flex-col min-h-0 ${matchId ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <div className="shrink-0 flex items-start justify-between px-4 pt-safe-or-5 pb-1.5">
          <div>
            <h1 className="font-serif text-app-fg" style={{ fontSize: 25, fontWeight: 400, lineHeight: 1, letterSpacing: '-.01em' }}>Chats</h1>
            {unreadTotal > 0 && (
              <p className="text-[11.5px] text-app-muted mt-[5px]">{unreadTotal} unread · across your trips</p>
            )}
          </div>
          <IconButton icon="edit" iconSize={16} label="New message" onClick={() => setSearch('')} />
        </div>

        {/* Search */}
        <div className="shrink-0 px-3.5 mt-2.5">
          <SearchInput value={search} onChange={setSearch} placeholder="Search guests or trips" />
        </div>

        {/* Filter pills */}
        <div className="shrink-0 flex gap-1.5 flex-wrap px-3.5 pt-3 pb-[11px]">
          {FILTERS.map((f) => (
            <Tab key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)} style={{ fontSize: 12, padding: '6px 13px' }}>
              {f.label}
            </Tab>
          ))}
        </div>

        {/* Conversations */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <ConversationList
            conversations={visible}
            activeMatchId={matchId}
            hostId={user?.id ?? ''}
            onSelect={handleSelect}
            loading={loading}
          />
        </div>
      </div>

      {/* Thread pane — always visible on desktop; mobile shows it only when a conversation is selected */}
      <div className={`flex-1 min-w-0 min-h-0 flex-col ${matchId ? 'flex' : 'hidden md:flex'}`}>
        {matchId ? (
          <div className="flex flex-col h-full min-h-0">
            <div className="md:hidden flex items-center gap-2 px-2 py-2 border-b border-app-border shrink-0 pt-safe">
              <button
                type="button"
                onClick={() => navigate('/host/chats')}
                aria-label="Back to conversations"
                className="min-w-11 min-h-11 flex items-center justify-center text-app-muted hover:text-app-fg transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              <span className="text-sm font-medium text-app-fg truncate">Chats</span>
            </div>
            <div className="flex-1 min-h-0">
              <HostChat />
            </div>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center">
            {loading ? (
              <Spinner />
            ) : (
              <p className="text-sm text-app-muted">Select a conversation to view the chat.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default HostChats;
